import { useState } from "react";
import { Check, Clock } from "lucide-react";
import { isOnOrder, isSoldOut, onOrderMessage } from "@/lib/patternAvailability";
import { textureSrc } from "@/lib/catalog";
import { familyNames, familyOf } from "@/lib/patternFamilies";

// Com muitos tecidos, agrupa por família (pelo identificador) e deixa filtrar uma família de cada vez.
const groupPatterns = (patterns) => {
  if (patterns.length <= 12) return [[null, patterns]];
  const out = familyNames(patterns.map((p) => p.id)).map((label) => [label, patterns.filter((p) => familyOf(p.id) === label)]);
  return out.length > 1 ? out : [[null, patterns]];
};

export const PatternSelector = ({ patterns, value, onChange, onPreview, legend = "Padrão", showPreview = true, productName = "" }) => {
  const [hover, setHover] = useState(null);
  const groups = groupPatterns(patterns);
  // Com muitos tecidos começa pela família do tecido escolhido (menos scroll no telemóvel); "Todos" mostra tudo.
  const [family, setFamily] = useState(() => (groups.length > 1 ? groups.find(([, list]) => list.some((p) => p.id === value))?.[0] ?? null : null));
  const visibleFamily = groups.some(([l]) => l === family) ? family : null;
  const current = patterns.find((p) => p.id === value);
  const shown = patterns.find((p) => p.id === (hover ?? value)) ?? current;
  const preview = (id) => {
    // Um tecido esgotado não se pré-visualiza: não se pode escolher.
    if (id && isSoldOut(patterns.find((p) => p.id === id))) id = null;
    setHover(id);
    onPreview?.(id);
  };
  return (
    <fieldset className="min-w-0">
      <legend className="mb-3 w-full text-sm font-semibold text-ink">{legend}</legend>
      {showPreview && <div className="mb-4 flex items-center gap-4 rounded-2xl bg-linen/60 p-3" data-testid="pattern-preview" aria-live="polite">
        {shown && <img src={textureSrc(shown.texture, 800)} alt="" className="h-28 w-28 shrink-0 rounded-xl sm:h-32 sm:w-32 object-cover shadow-[inset_0_0_0_1px_rgba(64,55,49,.1)]" data-testid="pattern-preview-image" />}
        <p className="min-w-0 text-sm">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-taupe-strong">{hover && hover !== value ? "A pré-visualizar" : productName ? `Aplicado ao teu ${productName.toLowerCase()}` : "Aplicado agora"}</span>
          <span data-testid="pattern-selected-name" className="block text-base font-medium leading-snug text-ink">{shown?.name ?? "Escolhe um padrão"}</span>
        </p>
      </div>}
      {isOnOrder(current) && (
        <p className="mb-4 flex items-start gap-2.5 rounded-2xl bg-rose-soft/80 px-3.5 py-2.5 text-[13px] leading-snug text-ink" role="status" data-testid="pattern-on-order-note">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-bordeaux" aria-hidden="true" />
          <span><strong className="font-semibold">{current.name}</strong> · {onOrderMessage(current)}</span>
        </p>
      )}
      {groups.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden" role="group" aria-label="Filtrar tecidos por família" data-testid="pattern-families">
          {[["Todos", null], ...groups.map(([l]) => [l, l])].map(([l, v]) => (
            <button key={l} type="button" aria-pressed={visibleFamily === v} data-testid={`pattern-family-${v ?? "todos"}`} onClick={() => setFamily(v)} className={`min-h-[40px] shrink-0 rounded-full border px-3.5 text-xs transition-colors ${visibleFamily === v ? "border-bordeaux bg-bordeaux text-white" : "border-ink/15 text-ink hover:border-bordeaux/50"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="space-y-6" role="radiogroup" aria-label="Padrões disponíveis para este produto">
        {groups.filter(([label]) => !visibleFamily || label === visibleFamily).map(([label, list]) => (
          <div key={label ?? "todos"}>
            {label && <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-taupe-strong">{label}</p>}
            <div className="grid grid-cols-3 gap-3.5 sm:grid-cols-4">
              {list.map((p) => {
                const active = p.id === value;
                const sold = isSoldOut(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-disabled={sold || undefined}
                    data-testid={`pattern-swatch-${p.id}`}
                    data-availability={sold ? "esgotado" : isOnOrder(p) ? "por-encomenda" : "disponivel"}
                    onClick={() => !sold && onChange(p.id)}
                    onMouseEnter={() => preview(p.id)}
                    onMouseLeave={() => preview(null)}
                    onFocus={() => preview(p.id)}
                    onBlur={() => preview(null)}
                    className={`group flex flex-col items-center gap-2 text-center ${sold ? "cursor-not-allowed" : ""}`}
                  >
                    <span
                      className={`relative block aspect-square w-full overflow-hidden rounded-xl border transition-[border-color,transform] duration-300 ${sold ? "" : "group-hover:-translate-y-0.5"} ${
                        active ? "border-bordeaux ring-2 ring-bordeaux/25" : "border-ink/10"
                      }`}
                    >
                      <img src={textureSrc(p.texture, 400)} alt="" loading="lazy" className={`h-full w-full object-cover ${sold ? "opacity-45 saturate-0" : ""}`} />
                      {sold && <span className="absolute inset-x-1 bottom-1 rounded-full bg-ivory/95 px-1 py-0.5 text-center text-[10px] font-semibold uppercase leading-none tracking-wide text-taupe-strong">Esgotado</span>}
                      {active && (
                        <span className="absolute bottom-1.5 right-1.5 grid h-5 w-5 place-items-center rounded-full bg-bordeaux text-white">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </span>
                    <span className={`text-[12px] leading-tight ${active ? "font-semibold text-ink" : sold ? "text-taupe/70" : "text-taupe"}`}>
                      {p.name}
                      {isOnOrder(p) && <span className="mt-0.5 block text-[10.5px] font-medium leading-tight text-bordeaux" data-testid={`pattern-on-order-${p.id}`}>Por encomenda</span>}
                      {sold && <span className="sr-only"> (esgotado)</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
};
